import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { apiService } from '../services/api';
import { getTodayInUserTimezone } from '../utils/dateUtils';

type ServiceType = 'mowing' | 'fertilizing' | 'snow_removal';

interface AddAllToScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SERVICE_TYPE_OPTIONS: { value: ServiceType; label: string }[] = [
  { value: 'mowing', label: 'Lawn Mowing/Trimming' },
  { value: 'fertilizing', label: 'Weed Control/Fertilizing' },
  { value: 'snow_removal', label: 'Snow Removal' },
];

const AddAllToScheduleDialog: React.FC<AddAllToScheduleDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [serviceType, setServiceType] = useState<ServiceType | ''>('');
  const [scheduledDate, setScheduledDate] = useState(getTodayInUserTimezone());
  const [resetRecurringSchedule, setResetRecurringSchedule] = useState<boolean | null>(null);
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'select-type' | 'select-date' | 'confirm-change-type' | 'confirm'>('select-type');

  const needsChangeTypeQuestion = serviceType === 'mowing' || serviceType === 'fertilizing';

  useEffect(() => {
    if (!open) {
      setServiceType('');
      setScheduledDate(getTodayInUserTimezone());
      setResetRecurringSchedule(null);
      setServiceIds([]);
      setError(null);
      setStep('select-type');
    }
  }, [open]);

  useEffect(() => {
    if (!open || !serviceType) {
      setServiceIds([]);
      return;
    }
    const fetchIds = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.get(
          '/service-requests?userType=provider&status=accepted,scheduled,in-progress,completed&limit=500'
        );
        if (!res.success || !res.data) {
          setError('Failed to load services');
          setServiceIds([]);
          return;
        }
        const requests = (res.data as any).serviceRequests || [];
        const filtered = requests
          .filter((sr: any) => sr.serviceType?.category === serviceType)
          .map((sr: any) => (sr._id || sr.id).toString());
        setServiceIds(filtered);
      } catch (err: any) {
        setError(err.message || 'Failed to load services');
        setServiceIds([]);
      } finally {
        setLoading(false);
      }
    };
    fetchIds();
  }, [open, serviceType]);

  const handleNext = () => {
    if (step === 'select-type' && serviceType) {
      setStep('select-date');
    } else if (step === 'select-date') {
      if (needsChangeTypeQuestion) {
        setStep('confirm-change-type');
      } else {
        setStep('confirm');
      }
    } else if (step === 'confirm-change-type' && resetRecurringSchedule !== null) {
      setStep('confirm');
    }
  };

  const handleBack = () => {
    if (step === 'select-date') setStep('select-type');
    else if (step === 'confirm-change-type') setStep('select-date');
    else if (step === 'confirm') setStep(needsChangeTypeQuestion ? 'confirm-change-type' : 'select-date');
  };

  const handleSubmit = async () => {
    if (serviceIds.length === 0) {
      setError('No services found for this type');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiService.post('/service-requests/schedule-batch', {
        serviceRequestIds: serviceIds,
        scheduledDate,
        resetRecurringSchedule: needsChangeTypeQuestion ? resetRecurringSchedule : false,
      });
      if (res.success && res.data) {
        const { successCount, failCount } = res.data as any;
        const msg = failCount > 0
          ? `Scheduled ${successCount} services. ${failCount} failed.`
          : `Successfully scheduled ${successCount} services for ${scheduledDate}.`;
        onSuccess();
        onClose();
        alert(msg);
      } else {
        setError((res as any).error || 'Batch schedule failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Batch schedule failed');
    } finally {
      setSubmitting(false);
    }
  };

  const serviceTypeLabel = SERVICE_TYPE_OPTIONS.find((o) => o.value === serviceType)?.label || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add All to Schedule</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 'select-type' && (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Service Type</InputLabel>
            <Select
              value={serviceType}
              label="Service Type"
              onChange={(e) => setServiceType(e.target.value as ServiceType)}
            >
              {SERVICE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {step === 'select-date' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select the date to schedule all {serviceTypeLabel} services:
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Finding services...</Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Found {serviceIds.length} {serviceTypeLabel} service{serviceIds.length !== 1 ? 's' : ''}.
              </Typography>
            )}
          </Box>
        )}

        {step === 'confirm-change-type' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              How should this schedule change apply?
            </Typography>
            <FormControl fullWidth>
              <InputLabel shrink>Change Type</InputLabel>
              <Select
                value={resetRecurringSchedule === null ? '' : resetRecurringSchedule ? 'permanent' : 'one-time'}
                label="Change Type"
                onChange={(e) => setResetRecurringSchedule(e.target.value === 'permanent')}
                displayEmpty
                renderValue={(v: unknown) => {
                  if (v === '' || v == null) return 'Select Change Type';
                  if (v === 'one-time') return 'One-time change';
                  if (v === 'permanent') return 'Permanent change';
                  return 'Select Change Type';
                }}
              >
                <MenuItem value="">Select Change Type</MenuItem>
                <MenuItem value="one-time">
                  One-time change – Next service date updates, but recurring schedule day stays the same
                </MenuItem>
                <MenuItem value="permanent">
                  Permanent change – All services permanently move to the new schedule day
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {step === 'confirm' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1">
              Schedule all {serviceIds.length} {serviceTypeLabel} service{serviceIds.length !== 1 ? 's' : ''} for{' '}
              <strong>{scheduledDate}</strong>?
            </Typography>
            {needsChangeTypeQuestion && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {resetRecurringSchedule
                  ? 'Permanent change – recurring schedule will be updated.'
                  : 'One-time change – original recurring schedule preserved.'}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step !== 'select-type' ? (
          <Button onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Box sx={{ flex: 1 }} />
        {step === 'confirm' ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || serviceIds.length === 0}
          >
            {submitting ? <CircularProgress size={24} /> : 'Add All'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (step === 'select-type' && !serviceType) ||
              (step === 'confirm-change-type' && resetRecurringSchedule === null) ||
              loading
            }
          >
            Next
          </Button>
        )}
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAllToScheduleDialog;
